/**
 * Le lien « Voir mon bilan » de l'email du matin de J8 (chantier C).
 *
 * Ce lien peut être ouvert n'importe quand et n'importe où : sur un téléphone où le
 * client n'est pas connecté, plusieurs jours après, après qu'il s'est abonné, ou dans
 * un navigateur connecté à un AUTRE compte (un ordinateur partagé, ou Adam qui teste).
 * Ce module décide, sans React, de ce que l'écran /fin-essai doit montrer, et où
 * revenir après la connexion. Vérifié par `scripts/check-trial-emails.mjs`.
 *
 * ⚠️ `accountTag` a un jumeau côté serveur (`supabase/functions/_shared/trialEmails.ts`) :
 * les deux doivent produire la même valeur — le script le contrôle.
 */

/** Paramètre de l'adresse qui dit à quel compte le lien est destiné. */
export const ACCOUNT_PARAM = "compte"

/** Paramètre de /auth qui dit où aller après la connexion. */
export const NEXT_PARAM = "next"

/**
 * Empreinte courte du compte, mise dans le lien de l'email : les 8 premiers caractères
 * de l'identifiant. Elle ne sert qu'à reconnaître « ce n'est pas le compte connecté »,
 * pas à authentifier — c'est la connexion qui le fait. 8 caractères hexadécimaux
 * suffisent à distinguer deux comptes sur un même appareil, sans exposer l'identifiant
 * entier dans une adresse qui passe par la messagerie.
 */
export function accountTag(userId: string): string {
  return userId.replace(/-/g, "").slice(0, 8).toLowerCase()
}

/** Les pages vers lesquelles /auth accepte de renvoyer après la connexion. */
const NEXT_ALLOWED_PATHS = new Set(["/fin-essai"])

/**
 * Valide la destination demandée après la connexion. Seuls des chemins internes
 * connus sont acceptés : `?next=https://ailleurs.example` ou `//ailleurs` ferait de
 * l'écran de connexion un tremplin vers n'importe quel site.
 */
export function safeNext(raw: string | null | undefined): string | null {
  if (!raw) return null
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.includes("\\")) return null
  let url: URL
  try {
    url = new URL(raw, "https://app.invalid")
  } catch {
    return null
  }
  if (url.origin !== "https://app.invalid") return null
  if (!NEXT_ALLOWED_PATHS.has(url.pathname)) return null
  return url.pathname + url.search
}

/** Adresse de connexion qui ramènera ici : `/auth?next=/fin-essai%3Fcompte%3D…`. */
export function authUrlReturningTo(pathname: string, search: string): string {
  const next = safeNext(pathname + search)
  return next ? `/auth?${NEXT_PARAM}=${encodeURIComponent(next)}` : "/auth"
}

/** Destination après la connexion, lue sur l'adresse de /auth. */
export function nextFromAuthSearch(search: string): string | null {
  return safeNext(new URLSearchParams(search).get(NEXT_PARAM))
}

export type TrialReportGate =
  /** Le lien est pour un autre compte que celui connecté ici. */
  | "other-account"
  /** L'essai court encore : pas de bilan avant le matin de J8. */
  | "in-trial"
  /** L'essai a été suivi d'un abonnement actif : le bilan d'essai est périmé. */
  | "subscribed"
  /** Rien à raconter : pas d'essai, ou abonnement terminé après conversion. */
  | "no-trial"
  /** Essai terminé sans abonnement : le bilan, puis les offres. */
  | "report"

export interface AccessForGate {
  source: string | null
  has_access: boolean
  in_trial: boolean
  trial_started_at: string | null
  trial_ends_at: string | null
}

export function trialReportGate(args: {
  /** `?compte=` de l'adresse, s'il y en a un. */
  linkTag: string | null
  userId: string
  access: AccessForGate | null
}): TrialReportGate {
  const { linkTag, userId, access } = args
  // D'abord le compte : sans ça, un navigateur connecté au compte admin afficherait
  // « Ton abonnement est actif » à quelqu'un qui vient voir le bilan de SON essai.
  if (linkTag && linkTag.toLowerCase() !== accountTag(userId)) return "other-account"
  if (!access) return "no-trial"
  if (access.in_trial && access.trial_ends_at) return "in-trial"
  // Le webhook Stripe passe `source` à `stripe` au paiement, et garde les dates de
  // l'essai. Sans ce test, un client abonné qui rouvre l'email voyait son bilan
  // d'essai et quatre offres, dont celle qu'il paie déjà, grisée.
  // Accès manuel sans essai (compte admin) : « ta semaine d'essai est derrière toi »
  // serait faux, d'où le test sur la date de début.
  if (access.source !== "trial") {
    return access.has_access && access.trial_started_at ? "subscribed" : "no-trial"
  }
  if (!access.trial_started_at || !access.trial_ends_at) return "no-trial"
  return "report"
}

/**
 * La pesée de fin d'essai ne se saisit que le matin du bilan (J8). Rouvert plus tard,
 * l'écran l'enregistrait quand même à la date de J8 : un poids du J11 se retrouvait
 * daté du J8 dans l'historique, et l'écart affiché mentait sur la semaine d'essai.
 */
export function weighInOpen(reportDay: string, todayParis: string): boolean {
  return todayParis === reportDay
}
